import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Grid, Typography, Button, TextField, ToggleButtonGroup, ToggleButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Stack
} from '@mui/material';
import { useHistory } from 'react-router-dom';

import { ConstantsContext, ThemeContext } from "../index";
import DashboardTopMenu from './DashboardTopMenu';

export default function RouteAI(props) {
  
  const history = useHistory();
  const [theme] = useContext(ThemeContext);
  const [staticText] = useContext(ConstantsContext);
  const apiPath = staticText.path_darts;

  const isAdmin   = props.userDetails?.is_admin === 1 || props.userDetails?.role === 'admin';
  const isAiAgent = props.userDetails?.is_ai_agent === 1;

  const ownerUserId = String(props?.userDetails?.id || props?.userDetails?.user_id || '');

  const [scope, setScope] = useState('mine'); // 'mine' | 'all'
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [prompts, setPrompts] = useState([]);
  const [search, setSearch] = useState('');

  // confirm dialog (delete/duplicate)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmKind, setConfirmKind] = useState(null); // 'delete' | 'duplicate'
 
  const [confirmTarget, setConfirmTarget] = useState(null);

  

  // ----- Add New Prompt (dialog) -----
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);

  // add next to your other "new*" states
  const [newPrompt, setNewPrompt] = useState("");


  const openNew = () => { setNewName(''); setNewDesc(''); setNewOpen(true); };
  const closeNew = () => setNewOpen(false);

  const createPrompt = () => {
    if (!newName.trim()) {
      alert('Please enter a name for the prompt.');
      return;
    }
    setCreating(true);

    const data = [
      { key: 'action',        value: 'set_prompt' },
      { key: 'loggined_token', value: props.userDetails?.loggined_token },
      { key: 'name',          value: newName.trim() },
      { key: 'description',   value: newDesc.trim() },
      { key: 'prompt', value: encodeURIComponent(newPrompt.trim()) },
      { key: 'status',        value: 'active' },
    ];

    console.log('data = ', data)

    props._apiConnect(apiPath, data, (res) => {
      setCreating(false);
      if (String(res?.status_code) === '2' && res?.prompt_id) {
        closeNew();
        history.push(`/AI/${res.prompt_id}`);
        fetchPrompts();
      } else {
        console.log(res?.msg || 'Failed to create prompt');
      }
    }, 'post'); // 👈 Force POST request
  };


  useEffect(() => { fetchPrompts(); /* eslint-disable-next-line */ }, [scope]);

  const fetchPrompts = () => {
    setLoading(true);
    setErrorMsg('');

    const data = [
      { key: 'action', value: scope === 'all' ? 'admin_get_prompts_list' : 'get_prompt_list' },
      { key: 'loggined_token', value: props.userDetails.loggined_token },
    ];
    if (scope === 'mine') data.push({ key: 'owner_user_id', value: ownerUserId });

    props._apiConnect(apiPath, data, (res) => {
      setLoading(false);
      if (String(res?.status_code) === '2') setPrompts(res?.prompts || []);
      else { setErrorMsg(res?.msg || 'Failed to load prompts'); setPrompts([]); }
    });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return prompts;
    return prompts.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q) ||
      (p.owner_username || '').toLowerCase().includes(q) ||
      String(p.id || '').includes(q)
    );
  }, [search, prompts]);

  // ---------- delete / duplicate ----------
  const openConfirm = (kind, prompt) => { setConfirmKind(kind); setConfirmTarget(prompt); setConfirmOpen(true); };
  const closeConfirm = () => { setConfirmOpen(false); setConfirmKind(null); setConfirmTarget(null); };

  const runConfirm = () => {
    if (!confirmKind || !confirmTarget) return;
    const action = confirmKind === 'delete' ? 'delete_prompt' : 'duplicate_prompt';
    const data = [
      { key: 'action', value: action },
      { key: 'loggined_token', value: props.userDetails.loggined_token },
      { key: 'prompt_id', value: String(confirmTarget.id) },
    ];
    setLoading(true);
    props._apiConnect(apiPath, data, () => {
      setLoading(false);
      closeConfirm();
      fetchPrompts();
    });
  };

  return (
    <Grid container spacing={2} style={{ width: '100%', margin: 0 }}>
      <DashboardTopMenu _apiLogOff={props._apiLogOff} userDetails={props.userDetails} />

      <Grid
        item
        xs={12}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          paddingRight: 10,
        }}
      >
        <Typography variant="h5" style={{ color: theme.foreground }}>
          AI – Prompts
        </Typography>

        {(isAiAgent || isAdmin) && (
          <ToggleButtonGroup
            value={scope}
            exclusive
            onChange={(_, val) => val && setScope(val)}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                color: theme.foreground,
                borderColor: theme.btn_color_border,
                "&.Mui-selected": {
                  backgroundColor: theme.btn_color,
                  color: theme.background,
                  borderColor: theme.btn_color,
                },
                "&:hover": {
                  backgroundColor:
                    theme.background === "#FFFFFF"
                      ? "rgba(0,0,0,0.08)"
                      : "rgba(255,255,255,0.1)",
                },
              },
            }}
          >
            <ToggleButton value="mine">My Prompts</ToggleButton>
            {isAdmin && <ToggleButton value="all">All Prompts (admin)</ToggleButton>}
          </ToggleButtonGroup>
        )}

        {/* NEW: Add prompt button */}
        <Button
          variant="contained"
          onClick={openNew}
          sx={{
            background: theme.btn_color,
            color: theme.background,
            "&:hover": { background: theme.btn_color },
          }}
        >
          Add New Prompt
        </Button>

        <div style={{ flex: 1 }} />

        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={theme.inputTextSx}
          InputLabelProps={{ style: { color: theme.foreground } }}
          inputProps={{ style: { color: theme.foreground } }}
        />
        <Button
          variant="outlined"
          onClick={fetchPrompts}
          disabled={loading}
          sx={{ color: theme.btn_color, borderColor: theme.btn_color_border }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Grid>


      {/* Table */}
      <Grid item xs={12} style={{ paddingRight: 10 }}>
        {errorMsg && (
          <div style={{ color: rgba('#d32f2f', 0.95), marginBottom: 8 }}>{errorMsg}</div>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, background: 'transparent' }}>
          <thead>
            <tr>
              <th style={thStyle(theme)}>ID</th>
              <th style={thStyle(theme)}>Name</th>
              <th style={thStyle(theme)}>Description</th>
              <th style={thStyle(theme)}>Owner</th>
              <th style={thStyle(theme)}>Linked Users</th>
              <th style={thStyle(theme)}>Created</th>
              <th style={thStyle(theme)}>Updated</th>
              <th style={thStyle(theme)}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p, i) => (
              <tr key={`${p.id}-${i}`}>
                <td style={tdStyle(theme)}>{p.id}</td>
                <td style={tdStyle(theme)}>{p.name}</td>
                <td style={tdStyle(theme)}>{p.description || '-'}</td>
                <td style={tdStyle(theme)}>
                  {p.owner_username || ''}{p.owner_username && p.owner_user_id ? ' · ' : ''}
                  {p.owner_user_id ? `#${p.owner_user_id}` : ''}
                </td>
                <td style={tdStyle(theme)}>{p.linked_users_count ?? (p.linked_users?.length || 0)}</td>
                <td style={tdStyle(theme)}>{p.created_at || '-'}</td>
                <td style={tdStyle(theme)}>{p.updated_at || '-'}</td>
                <td style={tdStyle(theme)}>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="View Prompt">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => history.push(`/AI/${p.id}`)}
                          sx={{ color: theme.btn_color, borderColor: theme.btn_color_border }}
                        >
                          View
                        </Button>
                      </span>
                    </Tooltip>

                    <Tooltip title="Duplicate Prompt">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openConfirm('duplicate', p)}
                          sx={{ color: theme.btn_color, borderColor: theme.btn_color_border }}
                        >
                          Duplicate
                        </Button>
                      </span>
                    </Tooltip>

                    <Tooltip title="Delete Prompt">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openConfirm('delete', p)}
                          sx={{ color: theme.btn_color, borderColor: theme.btn_color_border }}
                        >
                          Delete
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr>
                <td style={{ ...tdStyle(theme), textAlign: 'center' }} colSpan={8}>
                  No prompts found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Grid>

      {/* Confirm modal for delete/duplicate */}
      <Dialog open={confirmOpen} onClose={closeConfirm}>
        <DialogTitle>
          {confirmKind === 'delete' ? 'Delete Prompt' : 'Duplicate Prompt'}
        </DialogTitle>
        <DialogContent dividers>
          {confirmKind === 'delete'
            ? `Are you sure you want to delete prompt #${confirmTarget?.id} (${confirmTarget?.name})?`
            : `Create a duplicate of prompt #${confirmTarget?.id} (${confirmTarget?.name})?`}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancel</Button>
          <Button onClick={runConfirm} variant="contained" color={confirmKind === 'delete' ? 'error' : 'primary'}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Prompt modal */}
      <Dialog open={newOpen} onClose={closeNew} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Prompt</DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'grid', gap: 12 }}>
            <input
              type="text"
              placeholder="Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${theme.btn_color_border}`,
                borderRadius: 6,
                background: theme.background,
                color: theme.foreground,
              }}
            />
            <textarea
              placeholder="Description (optional)"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              rows={4}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${theme.btn_color_border}`,
                borderRadius: 6,
                background: theme.background,
                color: theme.foreground,
                resize: 'vertical',
              }}
            />
            {/* NEW: Prompt body */}
            <textarea
              placeholder="Prompt (free text)"
              value={newPrompt}
              onChange={(e) => setNewPrompt(e.target.value)}
              rows={8}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${theme.btn_color_border}`,
                borderRadius: 6,
                background: theme.background,
                color: theme.foreground,
                resize: 'vertical',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeNew}>Cancel</Button>
          <Button
            onClick={createPrompt}
            disabled={creating}
            variant="contained"
          >
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>


    </Grid>
  );
}

/* helpers & themed cell styles */
const rgba = (hex, a) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return hex;
  const r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};
const thStyle = (theme) => ({
  border: `1px solid ${rgba(theme.foreground, 0.25)}`,
  padding: '8px',
  backgroundColor: theme.infoPanelBackground,
  color: theme.foreground,
  textAlign: 'left',
});
const tdStyle = (theme) => ({
  border: `1px solid ${rgba(theme.foreground, 0.2)}`,
  padding: '8px',
  backgroundColor: 'transparent',
  color: theme.foreground,
});
